import typography from './typography'
import palette from './palette'
import zIndex from './zIndex'
import globals from './globals'
import elevation from './elevation'
import internalMixins from './internalMixins'
import { IconChevronDown, IconErrorTriangleSmall } from 'icons'

const components = {
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        error: <IconErrorTriangleSmall />,
      },
    },
    styleOverrides: {
      root: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        alignContent: 'center',
        gridGap: internalMixins.spacing(1),
        borderRadius: `${globals.borderRadius.main}px`,
        ...internalMixins.py([1]),
        ...internalMixins.px([1]),
        ...typography.dataMicro,
        width: 'fit-content !important',
      },
      icon: {
        padding: 0,
        margin: 0,
      },
      message: {
        padding: 0,
      },
      standardError: {
        color: palette.status.error,
        backgroundColor: internalMixins.hexToRgba(palette.status.error, .1),
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '&.shadow': {
          boxShadow: elevation[200],
          borderRadius: globals.borderRadius.more,
        },
        '&.outlined': {
          borderStyle: 'solid',
          borderColor: palette.border.neutralCool_300,
          borderWidth: globals.borderWidth.thick,
          transition: internalMixins.setTransition(['border-color']),
          borderRadius: globals.borderRadius.more,
          '&:hover': {
            borderColor: palette.border.neutralCool_600,
          },
          '&:focus-within': {
            borderColor: palette.border.tertiary,
          },
          'input': {
            height: '42px',
            minHeight: '42px',
          },
        },
        '&.rounded-full': {
          '&:not(.Mui-focused)': {
            '.MuiTextField-root': {
              borderRadius: '25px !important',
            },
          },
        },
        '&:focus-within': {
          '.MuiTextField-root': {
            // borderBottomLeftRadius: '0px !important',
            // borderBottomRightRadius: '0px !important',
          },
        },
      },
      listbox: {
        ...internalMixins.py([0.5]),
        ...internalMixins.px([0.5]),
      },
      option: {
        ...typography.bodySmall,
        color: palette.text.neutralCool_700,
        borderRadius: globals.borderRadius.doubleExtra,
        // borderRadius: globals.borderRadius.main,
        paddingTop: `10px !important`,
        paddingBottom: `10px !important`,
        paddingLeft: `15px !important`,
        paddingRight: `15px !important`,
        minHeight: '45px !important',
        position: 'relative',
        overflow: 'initial !important',
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
        transition: internalMixins.setTransition(['background-color', 'color']),
        '&:before': {
          content: '""',
          position: 'absolute',
          height: '100%',
          width: 5,
          top: 0,
          right: '100%',
          backgroundColor: palette.background.primary.main,
          opacity: 0,
          transition: internalMixins.setTransition(['opacity']),
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'transparent !important',
          '&.Mui-focused': {
            backgroundColor: 'transparent !important',
          },
          '&:before': {
            opacity: 1,
          },
        },
        '&:hover': {
          backgroundColor: `${palette.background.neutral_300.main} !important`,
        },
      },
      endAdornment: {
        display: 'none',
      },
      noOptions: {
        display: 'none',
      },
      input: {
        width: '100% !important',
        '&::placeholder': {
          color: palette.text.neutral_500,
          // color: palette.text.neutralCool_700,
          opacity: 1,
        },
        '&.Mui-disabled': {
          WebkitTextFillColor: palette.text.neutral_500,
        },
      },
      inputRoot: {
        flexWrap: 'nowrap',
        // display: 'inline-grid',
        // gridAutoFlow: 'column',
        // justifyContent: 'flex-start',
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '0 !important',
        },
      },
      popper: {
        '&.rounded-full': {
          '.MuiAutocomplete-paper': {
            // borderBottomLeftRadius: '25px',
            // borderBottomRightRadius: '25px',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          '&.disable-round': {
            '.MuiAutocomplete-paper': {
              
              borderRadius: '0 !important',
            },
          },
          // '.MuiAutocomplete-listbox': {
          //   padding: 0,
          // },
        },
      }
    },
  },
  MuiBadge: {
    styleOverrides: {
      root: {
        '&.static .MuiBadge-badge': {
          ...typography.labelMicroMedium,
          height: '20px',
          minWidth: '20px',
          padding: '0 6px',
          position: 'static',
          transform: 'none',
          zIndex: 0,
        },
        '&.small.static .MuiBadge-badge': {
          height: '16px',
          minWidth: '16px',
          padding: '0 4px',
        },
      },
      badge: {
        ...typography.labelAtomicMedium,
        height: '16px',
        minWidth: '16px',
        padding: '0 4px',
        right: '4px',
      },
      colorSecondary: {
        backgroundColor: palette.palette.secondary.a[500],

        '&.static .MuiBadge-badge': {
          backgroundColor: palette.background.inverse.main,
        },
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {},
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paperAnchorRight: ({ theme }) => theme.unstable_sx({
        maxWidth: '100vw',
        overflowX: 'hidden',
        borderTopLeftRadius: {
          xs: '20px',
        },
        borderBottomLeftRadius: {
          xs: '20px',
        },
      })
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background.paper.main,
        transition: internalMixins.setTransition([
          'background-color',
          'border-color',
          'box-shadow',
          'opacity',
          'visibility',
        ]),
        '&:hover': {
          backgroundColor: palette.background.neutral_300.main,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: 0,
      },
      sizeSmall: {
        height: 25,
        width: 25,
      },
      sizeMedium: {
        height: 32,
        width: 32,
      },
      sizeLarge: {
        height: 40,
        width: 40,
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        padding: '0 !important',
        backgroundColor: `${palette.background.main.main} !important`,
        minHeight: '',
        '&:before, &:after': {
          display: 'none',
        },
        input: {
          minHeight: globals.height.input,
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          borderRadius: globals.borderRadius.more,
          // borderRadius: '25px !important',
        },
        '&.MuiInputBase-adornedStart': {
          paddingLeft: '10px !important',
        },
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '10px !important',
        },
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        'button[class*="edgeEnd"]': {
          marginRight: '0 !important',
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        // ...typography.body,
        ...typography.data,
        letterSpacing: 0,
        borderRadius: `${globals.borderRadius.more}px !important`,
        // borderRadius: `${globals.borderRadius.full}px !important`,
        color: palette.text.main,
        '&.Mui-error': {
          borderColor: palette.border.error,
        },
        // '&.MuiOutlinedInput-root': {
        //   borderWidth: 0,
        // },
        // transition: internalMixins.setTransition(['color', 'background-color', 'opacity']),
      },
      input: {
        paddingTop: `0px !important`,
        paddingBottom: `0px !important`,
        // paddingTop: `${internalMixins.spacing(0.5)}px !important`,
        // paddingBottom: `${internalMixins.spacing(0.5)}px !important`,
        paddingLeft: `${internalMixins.spacing(1.5)}px !important`,
        paddingRight: `${internalMixins.spacing(1.5)}px !important`,
        height: globals.height.input,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 10,
        borderRadius: 10,
        '&:before': {
          opacity: 1,
          backgroundColor: palette.background.neutral_300.main,
        },
      },
      bar: {
        borderRadius: 10,
      },
    },
  },
  MuiOutlinedInput: {
    defaultProps: {
      disableUnderline: true,
      notched: false,
    },
    styleOverrides: {
      root: {
        borderStyle: 'solid',
        borderColor: palette.border.neutralCool_300,
        borderWidth: globals.borderWidth.thick,
        transition: internalMixins.setTransition(['border-color']),
      },
      notchedOutline: {
        display: 'none',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background.paper.main,
        color: palette.background.paper.text,
      },
      rounded: {
        borderRadius: globals.borderRadius.more,
      },
    },
  },
  MuiRating: {
    styleOverrides: {
      root: {
        gap: internalMixins.spacing(0.25),
      },
      iconEmpty: {
        color: palette.background.neutral_300.main,
      },
      iconFilled: {
        color: palette.palette.secondary.c[500],
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: IconChevronDown,
      // MenuProps: {
      //   anchorPosition: {
      //     top: 2,
      //   },
      // },
    },
    styleOverrides: {
      root: {
        minHeight: globals.height.input,
        transition: internalMixins.setTransition([
          'color',
          'background-color',
          'opacity',
        ]),
      },
      select: {
        ...internalMixins.py([0.5]),
        ...internalMixins.px([1.5]),
        boxSizing: 'border-box',
        height: '100%',
        display: 'inline-grid',
        alignItems: 'center',
        alignContent: 'center',
        gridAutoFlow: 'column',
        justifyContent: 'flex-start',
        gridGap: internalMixins.spacing(1),
        transition: internalMixins.setTransition([
          'color',
          'background-color',
          'opacity',
        ]),
        '.MuiMenuItem-root': {
          padding: 0,
          ...typography.data,
        },
      },
      icon: {
        top: 0,
        bottom: 0,
        right: internalMixins.spacing(1.5),
        margin: 'auto',
        color: 'inherit',
        transition: internalMixins.setTransition(['transform']),
      },
      iconOpen: {
        transform: 'rotate(-180deg)',
      },
    },
  },
  MuiMenu: {
    defaultProps: {},
    styleOverrides: {
      root: {
        '.menu-hide': {
          display: 'none',
        },
      },
      list: {
        ...internalMixins.py([0.5]),
        ...internalMixins.px([0.5]),
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        ...typography.bodySmall,
        color: palette.text.neutralCool_700,
        borderRadius: globals.borderRadius.main,
        ...internalMixins.py([1]),
        ...internalMixins.px([1.5]),
        minHeight: '45px',
        whiteSpace: 'initial',
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
        transition: internalMixins.setTransition(['background-color', 'color']),
        '&:before': {
          content: '""',
          position: 'absolute',
          height: '100%',
          width: 5,
          top: 0,
          right: '100%',
          backgroundColor: palette.background.primary.main,
          opacity: 0,
          transition: internalMixins.setTransition(['opacity']),
        },
        '&.Mui-selected, &.active': {
          backgroundColor: 'transparent',
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
          '&:before': {
            opacity: 1,
          },
        },
        '&:hover': {
          backgroundColor: `${palette.background.neutral_300.main} !important`,
        },
        '&[class*="MuiMultiSectionDigitalClockSection-item"]': {
          '&.Mui-selected': {
            color: `${palette.text.neutralCool_700} !important`,
            backgroundColor: 'transparent !important',
          },
        },
      },
    },
  },
  MuiSkeleton: {
    defaultProps: {
      variant: 'rounded',
    },
    styleOverrides: {
      root: {
        '&:not(.dark)': {
          backgroundColor: 'rgba(255, 255, 255, .2)',
        },
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        height: 5,
      },
      colorSecondary: {
        color: palette.background.inverse.main,
      },
      rail: {
        opacity: 1,
        backgroundColor: palette.background.neutral_300.main,
      },
      thumb: {
        height: 15,
        width: 15,
        '&:hover, &.Mui-focusVisible': {
          boxShadow: 'none !important',
        },
      },
      thumbColorSecondary: {
        backgroundColor: palette.background.main.main,
      },
      valueLabel: {
        ...typography.labelMicroMedium,
        letterSpacing: 0,
        backgroundColor: palette.background.inverse.main,
        borderRadius: globals.borderRadius.main,
        ...internalMixins.py([0.5]),
        ...internalMixins.px([1]),
        // ...(internalMixins.px([1.5])),
        '&:before': {
          width: 12.5,
          height: 12.5,
          borderBottomRightRadius: globals.borderRadius.main,
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      root: {
        '&.Mui-active, &.Mui-completed': {
          '.MuiStepConnector-line': {
            borderColor: palette.border.success,
          },
        },
      },
      horizontal: {
        left: '-50%',
        right: '50%',
      },
      line: {
        borderColor: palette.border.neutral_500,
        borderWidth: globals.borderWidth.thick,
        transition: internalMixins.setTransition(['border-color']),
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        marginTop: `3px !important`,
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {},
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '.MuiTableCell-root': {
          ...typography.dataSmall,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        color: palette.text.main,
        borderBottom: globals.border.neutral_200,
        // borderBottom: globals.border.neutral_500,
      },
      sizeMedium: {
        ...internalMixins.py(1.5),
        ...internalMixins.px(1),
      },
      head: {
        backgroundColor: palette.background.neutralCool_75.main,
        color: palette.background.neutralCool_75.text,
        borderTop: globals.border.neutral_500,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '.MuiTableCell-root': {
          color: 'inherit',
          ...typography.labelSmallTertiaryUpper,
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        ...internalMixins.px(1),
        'td:first-of-type, th:first-of-type': {
          paddingLeft: internalMixins.spacing(2),
        },
        'td:last-of-type, th:last-of-type': {
          paddingRight: internalMixins.spacing(2),
        },
        '&:not(.MuiTableRow-head):last-child td, &:not(.MuiTableRow-head):last-child th': {
          border: 0,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 1,
      },
      flexContainer: {
        gridGap: internalMixins.spacing(2.5),
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        paddingTop: 5,
        paddingBottom: 8,
        paddingLeft: 0,
        paddingRight: 0,
        minHeight: 1,
        minWidth: 1,
        color: palette.text.secondary,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '&.rounded-full': {
          '.MuiFilledInput-root': {
            borderRadius: '25px !important',
          },
          '&.squared-focus:focus-within': {
            '.MuiFilledInput-root': {
              borderBottomLeftRadius: '0px !important',
              borderBottomRightRadius: '0px !important',
            },
          },
        },
        '&.date-time-picker': {
          borderRadius: '6px !important',
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      popperArrow: {
        '.MuiTooltip-arrow': {
          color: palette.background.inverse.main,
        },
        '&[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
          marginTop: -15,
          width: 22,
          height: 15,
          '&:before': {
            borderTopLeftRadius: globals.borderRadius.main,
          },
        },
      },
      tooltip: {
        backgroundColor: palette.background.inverse.main,
        color: palette.background.inverse.text,
        borderRadius: globals.borderRadius.more,
        padding: 0,
        '&.paper': {
          backgroundColor: palette.background.paper.main,
          color: palette.background.paper.text,
          boxShadow: elevation[200],
          '.MuiTooltip-arrow': {
            color: palette.background.paper.main,
          },
        },
        '&.inverse': {
          backgroundColor: palette.background.inverse.main,
          color: palette.background.inverse.text,
          boxShadow: elevation[200],
          '.MuiTooltip-arrow': {
            color: palette.background.inverse.main,
          },
        },
        '&.money': {
          background: 'linear-gradient(272deg, rgba(25, 150, 140, 0.10) 0.86%, rgba(83, 203, 138, 0.10) 96.33%), #FFF',
          boxShadow: elevation[200],
          borderRadius: '20px',
          '.MuiTooltip-arrow': {
            color: '#EAF6F2',
          },
        },
      },
      popperInteractive: {
        zIndex: zIndex.modal,
      },
    },
  },
}

export default components
